import { useCallback } from 'react';
import { isEmpty } from '~/utils/lodash.utils';
import { useInjector } from '~/common/useInjector';
import { I$State } from '~/types/angular.types';
import { isReactFirst } from '~/utils/environment.utils';
import { useHistory } from 'react-router';
import {
    convertRouteToRegex,
    populateUrlParameters,
    cleanObjectParams
} from '~/utils/url.utils';
import { ValueOf } from 'type-fest';
import { AppStatesToUrl, IAppStates } from './useNavigation.consts';

export const vendorOnlyRoutes: Array<
    (typeof AppStatesToUrl)[keyof typeof AppStatesToUrl]
> = [
    AppStatesToUrl.root_core_navigation_home,
    AppStatesToUrl.root_core_navigation_settings_company,
    AppStatesToUrl.root_core_navigation_settings_company_general,
    AppStatesToUrl.root_core_navigation_settings_company_branding,
    AppStatesToUrl.root_core_navigation_settings_company_integrations,
    AppStatesToUrl.root_core_navigation_pipeline
];

interface BaseProps {
    route: Parameters<I$State['go']>[0];
    params?: Record<string, any>;
    stateParams?: Record<string, any>;
    options?: Parameters<I$State['go']>[2];
}

type Props = BaseProps;

export type NavigationFunction = (props: Props) => Promise<void>;

// Backwards compatibility for Angular navigation to '.', which navigates to the current route
function useDotNavigation() {
    const history = useHistory();

    const dotNavigate = useCallback(
        ({ params, stateParams, options }: BaseProps) => {
            const location = history.location;

            // For React, we'll reload the current route with new query parameters
            const currentPath = location.pathname;
            const searchParams = new URLSearchParams();

            // Add new params to query string
            if (params) {
                Object.entries(params).forEach(([key, value]) => {
                    if (
                        value !== undefined &&
                        value !== null &&
                        value !== false
                    ) {
                        searchParams.set(key, String(value));
                    }
                });
            }

            const newUrl = `${currentPath}${
                searchParams.toString() ? '?' + searchParams.toString() : ''
            }`;

            if (options?.location === 'replace') {
                history.replace(newUrl, stateParams);
            } else {
                history.push(newUrl, stateParams);
            }

            return Promise.resolve();
        },
        [history]
    );

    return dotNavigate;
}

function useAngularNavigation() {
    const { $state, $location, $rootScope } = useInjector([
        '$state',
        '$location',
        '$rootScope'
    ]);

    const baseNavigate = useCallback(
        ({ route, params, stateParams, options }: BaseProps) => {
            let _params;
            if (isEmpty(params) && isEmpty(stateParams)) {
                _params = undefined;
            } else {
                _params = {
                    ...(params || {}),
                    ...(stateParams || {})
                };
            }

            if (
                $state.get(route) &&
                $state.get(route).reloadOnSearch === false &&
                !isEmpty(_params)
            ) {
                const oldParams = $state.params;
                $location.search(_params);
                $rootScope.$broadcast('$OnlySearchChange', _params, oldParams);
                Object.assign($state.params, _params);
            }

            // this is so we don't have to fix all the tests that expect the method to be called without
            // unnecessary parameters (that are undefined)
            if (options !== undefined) {
                return $state.go(route, _params, options);
            } else if (_params !== undefined) {
                return $state.go(route, _params);
            } else {
                return $state.go(route);
            }
        },
        [$location, $rootScope, $state]
    );

    const navigate = useCallback(
        ({ route, params, stateParams, options }: Props) => {
            return baseNavigate({ route, params, stateParams, options });
        },
        [baseNavigate]
    );

    const navigateReplace = useCallback(
        ({ route, params, stateParams }: Props) => {
            return baseNavigate({
                route,
                params,
                stateParams,
                options: { location: 'replace' }
            });
        },
        [baseNavigate]
    );

    return {
        navigate,
        navigateReplace
    };
}

function useReactNavigation() {
    const history = useHistory();
    const dotNavigate = useDotNavigation();

    const baseNavigate = useCallback(
        ({ route, params, stateParams, options }: BaseProps) => {
            // Handle the special case of navigating to current route
            // @ts-expect-error - route can be '.' which is not in the AppStatesToUrl type
            if (route === '.') {
                return dotNavigate({ route, params, stateParams, options });
            }

            // Clean null/undefined parameters before navigation
            const cleanedParams = params
                ? cleanObjectParams(params)
                : undefined;

            const url = AppStatesToUrl[route];
            const adjustedUrl = populateUrlParameters(url, cleanedParams);

            if (history && adjustedUrl) {
                if (options?.reload) {
                    // Force a full page reload for the reload option
                    window.location.href = adjustedUrl;
                }

                if (options?.location === 'replace') {
                    history.replace(adjustedUrl, stateParams);
                } else {
                    history.push(adjustedUrl, stateParams);
                }
            } else {
                console.error('not routing', {
                    route,
                    params: cleanedParams,
                    options,
                    stateParams,
                    adjustedUrl
                });
            }

            // TODO(REACT_FIRST): remove this as its just here for backwards compatibility
            return Promise.resolve();
        },
        [history, dotNavigate]
    );

    const navigate = useCallback(
        ({ route, params, stateParams, options }: Props) => {
            return baseNavigate({ route, params, stateParams, options });
        },
        [baseNavigate]
    );

    const navigateReplace = useCallback(
        ({ route, params, stateParams }: Props) => {
            return baseNavigate({
                route,
                params,
                stateParams,
                options: { location: 'replace' }
            });
        },
        [baseNavigate]
    );

    return {
        navigate,
        navigateReplace
    };
}

export default function useNavigation() {
    const ngNavigation = useAngularNavigation();
    const navigation = useReactNavigation();

    return isReactFirst() ? navigation : ngNavigation;
}

export const getAppStateRouteFromUrl = (
    url: string
): ValueOf<IAppStates, keyof IAppStates> => {
    // Remove /app/ prefix if it exists
    const normalizedUrl = url.replace(/^\/app/, '');

    const appStateEntry = Object.entries(AppStatesToUrl).find(([, value]) => {
        if (value === '/') {
            return normalizedUrl === '/' || normalizedUrl === '';
        }

        const routeRegex = convertRouteToRegex(value);

        return routeRegex.test(normalizedUrl);
    });

    if (!appStateEntry || appStateEntry[1] === '/') {
        return 'root_core';
    }

    return appStateEntry[0] as ValueOf<IAppStates, keyof IAppStates>;
};
